// @ts-nocheck
/**
 * Guided walkthrough of the bank statement reconciliation lifecycle.
 * Vertical timeline: one step per row with a state pill and helper text.
 * Happy path (Classified → Auto/Manual matched) is shown first; exception
 * paths (Partially matched, Disputed) are clearly separated below.
 */

import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader } from '@ophelos/ui/components/card';
import { ArrowDown, CheckCircle2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BANK_STATEMENTS_LIFECYCLE } from './reconciliationLifecycles';

const STEP_HELPERS: Record<string, string> = {
  pending: 'Classify in Bank Statements (e.g. individual receipt, PSP receipt, client payout).',
  classified: 'Match the line to a case or transaction. When fully matched it moves to Auto matched or Manual matched.',
  auto_matched: 'Done — no action needed.',
  manual_matched: 'Done — no action needed.',
  partially_matched: 'Some items are matched; complete the remaining match, then it becomes Matched.',
  disputed: 'Needs resolution. Once resolved it becomes Matched.',
};

export interface BankStatementLifecycleFlowProps {
  proceedLabel?: string;
  proceedHref?: string;
}

function StepRow({
  stepId,
  label,
  needsReview,
  isTerminal,
  helper,
}: {
  stepId: string;
  label: string;
  needsReview: boolean;
  isTerminal: boolean;
  helper: string;
}) {
  return (
    <div className="stack gap-1 border p-2 rounded-lg shadow-xs">
      <Badge variant={isTerminal ? 'success' : needsReview ? 'warning' : 'subtle'}>
        {label}
        {isTerminal && <CheckCircle2 className="size-3 shrink-0" />}
      </Badge>

      <p className="text-xs text-muted-foreground leading-relaxed min-w-0">{helper}</p>
    </div>
  );
}

export function BankStatementLifecycleFlow({ proceedLabel, proceedHref }: BankStatementLifecycleFlowProps) {
  const { steps, label } = BANK_STATEMENTS_LIFECYCLE;
  const pending = steps.find((s) => s.id === 'pending');
  const classified = steps.find((s) => s.id === 'classified');
  const autoMatched = steps.find((s) => s.id === 'auto_matched');
  const manualMatched = steps.find((s) => s.id === 'manual_matched');
  const partiallyMatched = steps.find((s) => s.id === 'partially_matched');
  const disputed = steps.find((s) => s.id === 'disputed');

  return (
    <Card>
      <CardHeader>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label} lifecycle</p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          {/* 1. Pending classification */}
          {pending && (
            <StepRow
              stepId={pending.id}
              label={pending.label}
              needsReview={pending.needs_manual_review}
              isTerminal={false}
              helper={STEP_HELPERS.pending}
            />
          )}
          <div className="w-full flex justify-center">
            <ArrowDown className="size-4 text-muted-foreground shrink-0" />
          </div>

          {/* 2. Classified */}
          {classified && (
            <StepRow
              stepId={classified.id}
              label={classified.label}
              needsReview={classified.needs_manual_review}
              isTerminal={false}
              helper={STEP_HELPERS.classified}
            />
          )}

          <div className="w-full flex justify-center">
            <ArrowDown className="size-4 text-muted-foreground shrink-0" />
          </div>

          {/* 3. Happy path: Auto matched & Manual matched */}
          <div className="flex flex-col gap-3">
            {autoMatched && (
              <StepRow
                stepId={autoMatched.id}
                label={autoMatched.label}
                needsReview={false}
                isTerminal={true}
                helper={STEP_HELPERS.auto_matched}
              />
            )}
            {manualMatched && (
              <StepRow
                stepId={manualMatched.id}
                label={manualMatched.label}
                needsReview={false}
                isTerminal={true}
                helper={STEP_HELPERS.manual_matched}
              />
            )}
          </div>

          {/* Divider + exception paths */}
          <div className="pt-4 mt-1 border-t border-border/50 flex flex-col gap-3">
            <p className="text-xs font-medium text-muted-foreground">If not fully matched:</p>
            {partiallyMatched && (
              <StepRow
                stepId={partiallyMatched.id}
                label={partiallyMatched.label}
                needsReview={partiallyMatched.needs_manual_review}
                isTerminal={false}
                helper={STEP_HELPERS.partially_matched}
              />
            )}
            {disputed && (
              <StepRow
                stepId={disputed.id}
                label={disputed.label}
                needsReview={disputed.needs_manual_review}
                isTerminal={false}
                helper={STEP_HELPERS.disputed}
              />
            )}
          </div>
        </div>

        {proceedHref && (
          <div className="mt-4 pt-4">
            <Button variant="default" size="sm" asChild>
              <Link to={proceedHref}>
                {proceedLabel ?? 'Go to Bank statements'}
                <ExternalLink className="size-3.5 ml-1 shrink-0" />
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
